import { FormInput, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { ICashFlowForm } from 'services/cash-flow/types';

import { getSettlementPeriodName } from 'utils/global/name.utils';
import { lazy, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import CashFlowAddFileForm from './tabs/CashFlowAddFileForm';

import { useGetSettlementPeriodsLightList } from 'services/settlement';
import CashFlowData from './tabs/CashFlowData';
import CashFlowRepayments from './tabs/CashFlowRepayments';

export enum CASH_FLOW_TABS {
  data,
  repayments,
  file,
  contracts,
}

const validationSchema = object({}).shape({
  name: string().required(() => i18next.t('formik-error.required')),
  tax: number().required(() => i18next.t('formik-error.required')),
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  amountNet: lazy((_, options) =>
    number().when('amountGross', {
      is: (gross: number | null) => gross === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
  amountGross: lazy((_, options) =>
    number().when('amountNet', {
      is: (net: number | null) => net === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
});

export const cashFlowInitialValues: ICashFlowForm = {
  amountGross: null,
  amountNet: null,
  tax: null,
  name: '',
  settlementId: '',
  settlementName: '',
  fileUrl: '',
  repayments: [],
  contracts: [],
  settlementPeriod: null,
  settlementPeriodId: '',
};

interface ICashFlowFormProps extends FormikConfig<ICashFlowForm>, IFormProps {}

const CashFlowForm = ({
  customBackItem,
  status,
  tittle,
  backPath,
  topbarContent,
  disableTabs,
  initialValues,
  ...props
}: ICashFlowFormProps) => {
  const { t } = useTranslation();

  const isRepaymentsFromAllContracts = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  const settlementPeriods = useGetSettlementPeriodsLightList();
  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      {...props}>
      {({ values, setFieldValue }: IFormikProps<ICashFlowForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'label'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  <div className='flex w-full'>
                    {status?.displayMode ? null : (
                      <div className='w-full max-w-60'>
                        <FormInput.Text
                          name='name'
                          appBackground
                          label={t('input.label.name')}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                  <div>{topbarContent}</div>
                </div>
              }>
              <TabsNavigation
                disabled={disableTabs}
                tabs={[
                  {
                    value: CASH_FLOW_TABS.data,
                    name: 'data',
                  },
                  // {
                  //   value: CASH_FLOW_TABS.file,
                  //   name: 'file',
                  // },
                  ...(!disableTabs?.includes(CASH_FLOW_TABS.repayments)
                    ? [
                        {
                          value: CASH_FLOW_TABS.repayments,
                          name: 'repayments',
                        },
                      ]
                    : []),
                  ...(values.repaymentsFromAllContracts === false
                    ? [
                        {
                          value: CASH_FLOW_TABS.contracts,
                          name: 'contracts',
                        },
                      ]
                    : []),
                ]}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

CashFlowForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'data', element: <CashFlowData /> },
    { path: 'file', element: <CashFlowAddFileForm /> },
    { path: 'repayments', element: <CashFlowRepayments /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default CashFlowForm;
